/**
 * Admin slice (round-22 Phase D — per-record edit staging only).
 *
 * Round-22 Phase D removed the client-toggleable `isAdmin` flag
 * along with its `readInitialAdminFlag` bootstrap and
 * `adminFlagSet` action. Admin authorization now derives from
 * the Cognito ID token's `cognito:groups` claim via
 * `selectIsAdmin` (`src/store/selectors/admin-selectors.ts`) and
 * `useIsAdmin` (`src/lib/auth/useIsAdmin.ts`). The operator
 * grants + revokes admin capability out-of-band via
 * `grant-admin.sh` / `revoke-admin.sh`; the SPA never presents
 * a self-serve admin toggle. See
 * `.kiro/steering/no-vestigial-artifacts.md` for the deletion
 * rationale.
 *
 * What remains in this slice is the per-record edit staging
 * area — an in-memory workspace where an authenticated admin
 * queues field changes before dispatching a save. The
 * discriminated `kind` field on each entry lets the same slice
 * hold news / fact / release edits without a shape collision.
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

/** Citation row in the news edit form — one per source. */
export interface NewsEditSource {
  readonly name: string;
  readonly url: string;
}

/** Approval status the admin can toggle on the edit drawer. */
export type ApprovalStatus = 'pending' | 'approved' | 'rejected';

/** News record edit shape. */
export interface NewsEdit {
  readonly kind: 'news';
  readonly title: string;
  readonly body: string;
  readonly image: string;
  readonly sources: ReadonlyArray<NewsEditSource>;
  readonly status: ApprovalStatus;
}

/** Fact-of-the-day edit shape. */
export interface FactEdit {
  readonly kind: 'fact';
  readonly body: string;
  readonly image: string;
  readonly sources: ReadonlyArray<NewsEditSource>;
  readonly status: ApprovalStatus;
}

/** Purchase link row — vendor name + URL. */
export interface PurchaseLink {
  readonly vendor: string;
  readonly url: string;
}

/** Release edit shape. */
export interface ReleaseEdit {
  readonly kind: 'release';
  readonly title: string;
  readonly body: string;
  /** Cover image — the canonical 16:9 hero image used by `<ReleaseCard>`. */
  readonly image: string;
  /** Platform identifiers picked from the known `PlatformIcons` set. */
  readonly platforms: ReadonlyArray<string>;
  /** Gallery image URLs — rendered on the future Game-page detail surface. */
  readonly gallery: ReadonlyArray<string>;
  /** Video URLs (YouTube / direct mp4) — rendered as embeds on the Game-page surface. */
  readonly videos: ReadonlyArray<string>;
  /** Per-vendor purchase links. The first entry drives the release card's Purchase button. */
  readonly purchaseLinks: ReadonlyArray<PurchaseLink>;
  readonly status: ApprovalStatus;
}

/** Discriminated union of every staged-edit payload type. */
export type AdminEdit = NewsEdit | FactEdit | ReleaseEdit;

/** Per-record save-to-server lifecycle status. */
export type AdminSaveStatus = 'idle' | 'saving' | 'saved' | 'failed';

/** Per-record save-to-server error record (only populated on `'failed'`). */
export interface AdminSaveState {
  readonly status: AdminSaveStatus;
  readonly error?: string;
}

export interface AdminState {
  /** Staged edits keyed by content id (`record.id`). */
  readonly editsById: Record<string, AdminEdit>;
  /**
   * Save-to-server status per record id
   * (`content-ingestion-pipeline` Sub-R5.1). The DailyPage edit
   * drawer reads this to render a spinner / error banner while the
   * `PostContentV1` round-trip is in flight. Absent entries default
   * to `{status: 'idle'}` at the selector layer.
   */
  readonly saveById: Record<string, AdminSaveState>;
}

const initialState: AdminState = {
  editsById: {},
  saveById: {},
};

export const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    /** Save a per-record edit override. */
    adminEditSaved(state, action: PayloadAction<{ id: string; edit: AdminEdit }>) {
      // Cast through `unknown` because Immer's draft wrapper strips
      // the `readonly` modifiers on nested arrays once the draft
      // is written, but TypeScript doesn't see through the call site.
      state.editsById[action.payload.id] = action.payload.edit as unknown as typeof state.editsById[string];
    },
    /** Discard a per-record edit override. */
    adminEditCleared(state, action: PayloadAction<{ id: string }>) {
      delete state.editsById[action.payload.id];
      delete state.saveById[action.payload.id];
    },
    /** Mark a save-to-server call as in-flight for one record. */
    adminSaveRequested(state, action: PayloadAction<{ id: string }>) {
      state.saveById[action.payload.id] = { status: 'saving' };
    },
    /**
     * Mark a save-to-server call as succeeded. Clears the staged
     * edit for the record — the follow-up `fetchContent` refetches
     * from the server so the local override is no longer needed.
     */
    adminSaveSucceeded(state, action: PayloadAction<{ id: string }>) {
      state.saveById[action.payload.id] = { status: 'saved' };
      delete state.editsById[action.payload.id];
    },
    /** Mark a save-to-server call as failed. */
    adminSaveFailed(state, action: PayloadAction<{ id: string; error: string }>) {
      state.saveById[action.payload.id] = { status: 'failed', error: action.payload.error };
    },
    /** Reset the save-status entry for one record (e.g., on drawer close). */
    adminSaveReset(state, action: PayloadAction<{ id: string }>) {
      delete state.saveById[action.payload.id];
    },
  },
});

export const {
  adminEditSaved,
  adminEditCleared,
  adminSaveRequested,
  adminSaveSucceeded,
  adminSaveFailed,
  adminSaveReset,
} = adminSlice.actions;

export default adminSlice.reducer;
